import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IAddSubFliEvent } from '../interfaces/IAddSubFliEvent.interface';
import { IAssignAccountEvent } from '../interfaces/IAssignAccountEvent.interface';
import { IGroupAccountMappingComponentDelegate } from '../interfaces/IGroupAccountMappingComponentDelegate.interface';
import { IRemoveAccountEvent } from '../interfaces/IRemoveAccountEvent.interface';
import { IAddFliEvent } from '../interfaces/add-fli-event.interface';
import { IAddSubtotalEvent } from '../interfaces/add-subtotal-event.interface';
import { ISplitAccountValues } from '../interfaces/split-account-values.interface';
import { IUncoveredShortfallSpecifications } from '../interfaces/uncovered-shortfall-specifications.interface';
import { AccountMappingEngine } from './account-mapping-engin';

export function generateAccountMappingComponentDelegate(
    getEngine: () => AccountMappingEngine,
    getAccountMappingMode: () => boolean,
    setAccountMappingMode: (mode: boolean) => void,
    getTopGroup: () => IAccountMappingTemplateGroup | undefined,
): IGroupAccountMappingComponentDelegate {
    return {
        renameItem: (item, newCaption) => getEngine().renameItem(item, newCaption),
        renameGroup: (group, newTitle) => getEngine().renameGroup(group, newTitle),
        updateFli: (updatedFli, oldFli, newParentGroup, oldParentGroup) => {
            getEngine().updateFli(updatedFli, oldFli, newParentGroup, oldParentGroup);
            getEngine().calculateAll();
        },
        updateSubtotal: (updatedSubtotal, oldSubtotal, newIndex, oldIndex, oldParentGroup) => {
            getEngine().updateSubtotal(updatedSubtotal, oldSubtotal, newIndex, oldIndex, oldParentGroup);
            getEngine().calculateAll();
        },

        expandAll: false,
        collapseAll: false,
        get allFlis() {
            return getEngine().getAllFlis(getTopGroup());
        },
        // this refers to the delegate
        get templateGroup() {
            return getTopGroup();
        },
        get accountMappingMode() {
            return getAccountMappingMode();
        },
        assignSelectedAccounts: (event: IAssignAccountEvent) => {
            if (event.item.type === ItemType.SUB_FLI || event.item.type === ItemType.OF_THAT_ITEM) {
                getEngine().putDraggedAccountsIntoSubFLI(event.parent, event.item);
            } else if (event.item.type === ItemType.FLI) {
                getEngine().putDraggedAccountsIntoFLI(event.item);
            }
            setAccountMappingMode(false);
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        removeAccounts: (event: IRemoveAccountEvent) => {
            if (event.item.type === ItemType.SUB_FLI || event.item.type === ItemType.OF_THAT_ITEM) {
                getEngine().removeAllAccountsFromSubFli(event.item, event.accountTopGroupName);
            } else if (event.item.type === ItemType.FLI) {
                getEngine().removeAllAccountsFromFli(event.item, event.accountTopGroupName);
            }
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        removeAccountFromFLI: (
            item: IAccountMappingTemplateGroupItemFLI,
            index: number,
            accountTopGroupName?: string,
        ) => {
            getEngine().removeAccountFromFLI(item, index, accountTopGroupName);
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        removeAccountFromSubFLI: (item: IAccountMappingSubFLI, index: number, accountTopGroupName: string) => {
            getEngine().removeAccountFromSubFLI(item, index, accountTopGroupName);
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        removeItemFromParentGroup: (item: AccountMappingTemplateGroupItem, removeAccounts: boolean) => {
            getEngine().removeItemFromParentGroup(item, removeAccounts);
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        removeGroup: (group: IAccountMappingTemplateGroup) => {
            getEngine().removeGroup(group);
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        addSubFli: (event: IAddSubFliEvent) => {
            getEngine().addSubFli(event.parent, event.subFli);
            getEngine().calculateAll();
        },
        addFli: (event: IAddFliEvent) => {
            getEngine().addFli(event.parent, event.fli);
            getEngine().calculateAll();
        },
        addGroup: (parentGroup: IAccountMappingTemplateGroup, group: IAccountMappingTemplateGroup) => {
            getEngine().addGroup(parentGroup, group);
            getEngine().calculateAll();
        },
        addSubtotalAfterIndex: (event: IAddSubtotalEvent) => {
            getEngine().addSubtotalAfterIndex(
                event.parent,
                event.subtotal,
                event.index,
                event.placeSubtotalAsFirstItem,
            );
            getEngine().calculateAll();
        },

        addOrUpdateAccount: (
            oldAccount: IAccount | undefined,
            updatedAccount: IAccount,
            item: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
            accountIndex: number,
        ): void => {
            getEngine().addOrUpdateAccount(oldAccount, updatedAccount, item, accountIndex);
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        getAddedAccountRowNumber: (): number => {
            return getEngine().getAddedAccountRowNumber();
        },
        getFlisAndSubFlisForSwitchAccount: (
            initialTopGroupName: string,
        ): (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[] => {
            return getEngine().getFlisAndSubFlisForSwitchAccount(initialTopGroupName);
        },
        getSplitAccountValues: (
            originalClosingValue: number,
            originalOpeningValue: number,
            topParentGroupName: string,
        ): ISplitAccountValues => {
            return getEngine().getSplitAccountValues(originalClosingValue, originalOpeningValue, topParentGroupName);
        },
        splitAccount: (
            account: IAccount,
            accountParentItem: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
            accountIndex: number,
            accountTopParentGroupName: string,
            splitParentItem: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
        ): void => {
            getEngine().splitAccount(
                account,
                accountParentItem,
                accountIndex,
                accountTopParentGroupName,
                splitParentItem,
            );
            getEngine().calculateAll();
            getEngine().recalculateUncoveredShortfall();
        },
        removeAccountByGroupName: (groupName: string, accountNumber: string): void => {
            getEngine().removeAccountByGroupName(groupName, accountNumber);
        },
        getFlisAndSubFlisOfGroups: (
            group: IAccountMappingTemplateGroup,
        ): (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[] => {
            return getEngine().getFlisAndSubFlisOfGroups(group);
        },
        getAllFlisInMappingTemplate: (): IAccountMappingTemplateGroupItemFLI[] => {
            return getEngine().getAllFlisInMappingTemplate();
        },
        getUncoveredShortfallSpecifications: (): IUncoveredShortfallSpecifications => {
            return getEngine().getUncoveredShortfallSpecifications();
        },
        applyUncoveredShortfall: (): void => {
            getEngine().applyUncoveredShortfall();
            getEngine().calculateAll();
        },
        reorderGroupItems: (): void => {
            getEngine().calculateAll();
        },

        getItemIndexInsideGroup: (
            item: AccountMappingTemplateGroupItem,
            parent: IAccountMappingTemplateGroup,
        ): number => {
            return getEngine().getItemIndexInsideGroup(item, parent);
        },

        getSubGroupIndexInsideGroup: (
            item: IAccountMappingTemplateGroup,
            parent: IAccountMappingTemplateGroup,
        ): number => {
            return getEngine().getSubGroupIndexInsideGroup(item, parent);
        },

        getItemIndexInsideSortedItems: (
            item: IAccountMappingTemplateGroup,
            parent: IAccountMappingTemplateGroup,
        ): number => {
            return getEngine().getItemIndexInsideSortedItems(item, parent);
        },

        getSubfliOrOfThatItemIndexInsideFli: (
            item: AccountMappingAccountItem,
            parent: IAccountMappingTemplateGroupItemFLI,
        ): number => {
            return getEngine().getSubfliOrOfThatItemIndexInsideFli(item, parent);
        },

        getAccountIndexInsideFli: (account: IAccount, parent: IAccountMappingTemplateGroupItemFLI): number => {
            return getEngine().getAccountIndexInsideFli(account, parent);
        },

        insertItemInItemsOfGroup: (
            newItem: AccountMappingTemplateGroupItem,
            siblingItemIndex: number,
            parent: IAccountMappingTemplateGroup,
        ): void => {
            getEngine().insertItemInItemsOfGroup(newItem, siblingItemIndex, parent);
        },
        insertItemInFli: (
            newItem: AccountMappingAccountItem,
            siblingItemIndex: number,
            parent: IAccountMappingTemplateGroupItemFLI,
        ): void => {
            getEngine().insertItemInFli(newItem, siblingItemIndex, parent);
        },
        insertItemInGroupsOfGroup: (
            newItem: IAccountMappingTemplateGroup,
            siblingItemIndex: number,
            parent: IAccountMappingTemplateGroup,
        ): void => {
            getEngine().insertItemInGroupsOfGroup(newItem, siblingItemIndex, parent);
        },

        insertItemInSortedItems: (
            newItem: IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem,
            siblingItemIndex: number,
            parent: IAccountMappingTemplateGroup,
        ): void => {
            getEngine().insertItemInSortedItems(newItem, siblingItemIndex, parent);
        },

        getAllGroupsNamesToGroupsMap: (): Map<string, IAccountMappingTemplateGroup> => {
            return getEngine().getAllGroupsNamesToGroupsMap();
        },

        getAccountTopGroupName: (targetAccount: IAccount): string | undefined => {
            return getEngine().getAccountTopGroupName(targetAccount);
        },

        getOfThatItemSiblings: (ofThatItem: IAccountMappingSubFLI): IAccountMappingSubFLI[] => {
            return getEngine().getOfThatItemSiblings(ofThatItem);
        },

        cloneAccount: (
            masterAccount: IAccount,
            masterParent: IAccountMappingSubFLI,
            targetOfThatItems: IAccountMappingSubFLI[],
            index: number,
        ): void => {
            getEngine().cloneAccount(masterAccount, masterParent, targetOfThatItems, index);
            getEngine().calculateAll();
        },
    };
}
